import React, { useEffect, useState } from "react";
import { Box, PageLayout, Text, ActionList, ActionMenu } from "@primer/react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  SyncIcon,
  KebabHorizontalIcon,
  CheckIcon,
  XIcon,
} from "@primer/octicons-react";
import { Dialog } from "@primer/react/drafts";
import { useSelector } from "react-redux";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import MyButton from "../../components/ui/button/Button";
import DataTable from "../../components/ui/data-table";
import Button from "../../components/ui/button";
import { kyThiApi } from "../../api/kyThiApi";
import { KyThiItemResponse } from "../../models/response/ky-thi/KyThiItemResponse";
import { ex_kythi } from "../../models/response/ky-thi/ex_kythi";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import KyThiDetailModal from "./detail_modal.js.";

const KyThi: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [kyThis, setKyThis] = useState([]);
  const [selectedKyThi, setSelectedKyThi] = useState<ex_kythi>();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const loadKyThis = async () => {
    setIsLoading(true);
    try {
      const res = await kyThiApi.selectAll();
      if (res.is_success) {
        const filteredData = res.data.filter(
          (x: any) => x.dm_coso_id === dm_coso_id && x.nam_hoc === nam_hoc
        );
        setKyThis(filteredData);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải dữ liệu");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadKyThis();
  }, [dm_coso_id, nam_hoc]);

  const handleDelete = async (id: number) => {
    try {
      const res = await kyThiApi.delete(id);
      if (res.is_success) {
        await loadKyThis();
        NotifyHelper.Success("Xóa thành công");
        setShowDeleteConfirm(false);
      } else {
        NotifyHelper.Error(res.message || "Có lỗi");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
  };

  return (
    <Box p={3}>
      <DataTable
        subTitle={`${translate("Base.Label.TotalCount")}: ${kyThis.length}`}
        height="calc(100vh - 200px)"
        data={kyThis}
        isLoading={isLoading}
        title="Danh sách kỳ thi"
        searchEnable
        paging={{ enable: true }}
        actionComponent={
          <Box sx={{ display: "flex" }}>
            <MyButton
              text="Thêm kỳ thi"
              leadingVisual={PlusIcon}
              size="medium"
              variant="primary"
              onClick={() => {
                setSelectedKyThi({
                  id: 0,
                  nam_hoc: nam_hoc,
                  ten_ky_thi: "",
                  ten_ky_thi_en: "",
                  dm_truong_id: 0,
                  dm_he_id: 0,
                  dm_khoi_id: 0,
                  ngay_thi: null,
                  thoi_gian_tu: null,
                  thoi_gian_den: null,
                  da_ket_thuc: false,
                });
                setShowDetailModal(true);
              }}
            />
            <MyButton
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              size="medium"
              sx={{ ml: 1 }}
              onClick={loadKyThis}
            />
          </Box>
        }
        columns={[
          {
            id: "cmd",
            caption: "",
            width: "50px",
            align: "center",
            cellRender: (data: any) => (
              <ActionMenu>
                <ActionMenu.Button
                  icon={KebabHorizontalIcon}
                  variant="invisible"
                  sx={{ m: -1 }}
                >
                  &nbsp;
                </ActionMenu.Button>
                <ActionMenu.Overlay width="auto">
                  <ActionList>
                    <ActionList.Item
                      onSelect={() => {
                        setSelectedKyThi(data);
                        setShowDetailModal(true);
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <PencilIcon />
                      </ActionList.LeadingVisual>
                      {translate("Base.Label.Edit")}
                    </ActionList.Item>
                    <ActionList.Item
                      variant="danger"
                      onSelect={() => {
                        setSelectedKyThi(data);
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <TrashIcon />
                      </ActionList.LeadingVisual>
                      {translate("Base.Label.Delete")}
                    </ActionList.Item>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            ),
          },
          {
            align: "center",
            dataField: "da_ket_thuc",
            caption: translate("Đã kết thúc"),
            cellRender: (data: any) => (
              <input
                type="checkbox"
                checked={data.da_ket_thuc}
                readOnly
                style={{ cursor: 'default' }}
              />
            ),
          },
          {
            dataField: "ten_ky_thi",
            caption: translate("Tên kỳ thi"),
          },
          {
            dataField: "ten_ky_thi_en",
            caption: translate("Tên kỳ thi (En)"),
          },
          {
            dataField: "ten_truong",
            caption: translate("Tên trường"),
          },
          {
            dataField: "ten_he",
            caption: translate("Tên hệ"),
          },
          {
            dataField: "ten_khoi",
            caption: translate("Tên khối"),
          },
          {
            dataField: "ngay_thi_converted",
            caption: translate("Ngày bắt đầu"),
          },
          {
            dataField: "thoi_gian_tu_converted",
            caption: translate("Bắt đầu"),
          },
          {
            dataField: "thoi_gian_den_converted",
            caption: translate("Kết thúc"),
          },
         
        ]}
      />

      {showDetailModal && selectedKyThi && (
        <KyThiDetailModal
          id={selectedKyThi.id}
          onCancel={() => setShowDetailModal(false)}
          onSuccess={() => {
            setShowDetailModal(false);
            loadKyThis();
          }}
        />
      )}

      {showDeleteConfirm && selectedKyThi && (
        <Dialog
          title={translate("Lưu ý")}
          onClose={() => setShowDeleteConfirm(false)}
        >
          <Dialog.Body>
            {translate("Bạn có chắc chắn muốn xóa dữ liệu ?")}
          </Dialog.Body>
          <Dialog.Footer>
            <Button onClick={() => setShowDeleteConfirm(false)}>
              {translate("Base.Label.Cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={() => handleDelete(selectedKyThi.id)}
            >
              {translate("Base.Label.Delete")}
            </Button>
          </Dialog.Footer>
        </Dialog>
      )}
    </Box>
  );
};

export default KyThi;
